import { Play, Save, ToggleLeft, ToggleRight } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { DEFAULT_JOB_OWNER, type IntegrationJob, type IntegrationStatus, type Severity, type SourceType } from "../../shared/types";
import { useAuthUser } from "../auth/AuthProvider";
import { SeverityBadge } from "../components/common/SeverityBadge";
import { StateBlock } from "../components/common/StateBlock";
import { StatusChip } from "../components/common/StatusChip";
import { getJobs, saveJob, withDefaultJobOwner } from "../services/jobService";
import { rerunAcrJob } from "../services/rerunService";

interface SettingsJobsPageProps {
  refreshKey: number;
}

type FormMessage = {
  type: "success" | "error";
  text: string;
};

const sourceTypes: SourceType[] = ["SYNAPSE", "ACR", "SQL_ETL", "POWER_BI", "MARKETING", "CUSTOM", "AZURE_MONITOR"];
const severityOptions: Severity[] = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
const statusOptions: IntegrationStatus[] = [
  "SCHEDULED",
  "RUNNING",
  "SUCCEEDED",
  "FAILED",
  "CANCELLED",
  "MISSING",
  "LATE",
  "LONG_RUNNING",
  "UNKNOWN"
];

export function SettingsJobsPage({ refreshKey }: SettingsJobsPageProps) {
  const [jobs, setJobs] = useState<IntegrationJob[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string>();
  const [draft, setDraft] = useState<IntegrationJob>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);
  const [rerunMessage, setRerunMessage] = useState<string>();
  const [message, setMessage] = useState<FormMessage>();
  const { hasAnyRole } = useAuthUser();
  const hasRerunRole = hasAnyRole(["HealthDashboard.Operator", "HealthDashboard.Admin"]);
  const hasConfigRole = hasAnyRole(["HealthDashboard.Admin"]);
  const canRerunSelectedJob = Boolean(
    draft &&
      draft.sourceType === "ACR" &&
      hasRerunRole &&
      draft.status !== "RUNNING"
  );
  const rerunUnavailableReason = !hasRerunRole
    ? "Operator or Admin role is required."
    : draft?.sourceType !== "ACR"
      ? "Rerun is currently available for ACR Container Apps jobs."
      : draft?.status === "RUNNING"
        ? "This job is already running."
        : "Select an ACR job to request a rerun.";

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    getJobs()
      .then((nextJobs) => {
        if (!isMounted) {
          return;
        }
        setJobs(nextJobs);
        setSelectedJobId(undefined);
        setDraft(undefined);
        setMessage(undefined);
        setRerunMessage(undefined);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        setJobs([]);
        setSelectedJobId(undefined);
        setDraft(undefined);
        setRerunMessage(undefined);
        setMessage({
          type: "error",
          text: error instanceof Error ? error.message : "Job configuration could not be loaded."
        });
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [refreshKey]);

  function selectJob(job: IntegrationJob) {
    if (selectedJobId === job.id) {
      setSelectedJobId(undefined);
      setDraft(undefined);
      setMessage(undefined);
      setRerunMessage(undefined);
      return;
    }

    setSelectedJobId(job.id);
    setDraft(withDefaultJobOwner(job));
    setMessage(undefined);
    setRerunMessage(undefined);
  }

  function updateDraft<K extends keyof IntegrationJob>(key: K, value: IntegrationJob[K]) {
    setDraft((current) => (current ? withDefaultJobOwner({ ...current, [key]: value }) : current));
  }

  async function handleSave() {
    if (!draft) {
      return;
    }

    setIsSaving(true);
    setMessage(undefined);

    try {
      const saved = withDefaultJobOwner(await saveJob(withDefaultJobOwner(draft)));
      setJobs((current) => {
        const exists = current.some((job) => job.id === saved.id);
        const nextJobs = exists ? current.map((job) => (job.id === saved.id ? saved : job)) : [...current, saved];
        return nextJobs.map(withDefaultJobOwner);
      });
      setSelectedJobId(saved.id);
      setDraft(saved);
      setMessage({ type: "success", text: "Job configuration saved to the API." });
    } catch (error) {
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Job configuration save failed."
      });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRerun() {
    if (!draft || isRerunning) {
      return;
    }

    const confirmed = window.confirm(`Start a new Container Apps job execution for ${draft.name}?`);
    if (!confirmed) {
      return;
    }

    setIsRerunning(true);
    setRerunMessage(undefined);
    setMessage(undefined);

    try {
      const result = await rerunAcrJob(draft.id);
      setRerunMessage(`Rerun requested for ${result.containerAppJobName}.`);
      setMessage({ type: "success", text: `Rerun requested for ${result.containerAppJobName}.` });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unable to request job rerun.";
      setRerunMessage(errorMessage);
      setMessage({ type: "error", text: errorMessage });
    } finally {
      setIsRerunning(false);
    }
  }

  if (isLoading) {
    return <StateBlock type="loading" title="Loading job settings" message="Reading job configuration." />;
  }

  return (
    <section className="page-stack">
      <div className="page-heading">
        <div>
          <p>Configuration</p>
          <h2>Settings / Jobs</h2>
          <p>{jobs.filter((job) => job.isActive).length} active / {jobs.filter((job) => !job.isActive).length} inactive</p>
        </div>
        {message && <span className={message.type === "error" ? "error-pill" : "success-pill"}>{message.text}</span>}
      </div>

      <div className="settings-layout">
        <section className="panel table-panel">
          <div className="table-scroll">
            <table className="data-table compact">
              <thead>
                <tr>
                  <th>Job Name</th>
                  <th>Source Type</th>
                  <th>Source Name</th>
                  <th>Expected Time</th>
                  <th>Cron Expression</th>
                  <th>Grace Minutes</th>
                  <th>Expected Duration Minutes</th>
                  <th>Severity</th>
                  <th>Owner</th>
                  <th>Is Active</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 && (
                  <tr>
                    <td colSpan={10}>No job configuration is available.</td>
                  </tr>
                )}
                {jobs.map((job) => (
                  <Fragment key={job.id}>
                    <tr
                      className={selectedJobId === job.id ? "selected-row" : ""}
                      onClick={() => selectJob(job)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          selectJob(job);
                        }
                      }}
                      tabIndex={0}
                    >
                      <td><strong>{job.name}</strong></td>
                      <td>{job.sourceType}</td>
                      <td>{job.sourceName}</td>
                      <td>{job.expectedStartTime}</td>
                      <td>{job.cronExpression}</td>
                      <td>{job.graceMinutes}</td>
                      <td>{job.expectedDurationMinutes}</td>
                      <td><SeverityBadge severity={job.severity} /></td>
                      <td>{job.owner}</td>
                      <td>{job.isActive ? "Yes" : "No"}</td>
                    </tr>
                    {selectedJobId === job.id && draft && (
                      <tr className="settings-editor-row">
                        <td className="settings-editor-cell" colSpan={10}>
                          <section className="settings-accordion-panel">
                            <div className="panel-heading">
                              <div>
                                <p>Job configuration</p>
                                <h3>Edit Job</h3>
                              </div>
                              <StatusChip status={draft.status} />
                            </div>

                            <div className="form-grid">
                              <label>
                                Job ID
                                <input value={draft.id} readOnly />
                              </label>
                              <label>
                                Job Name
                                <input disabled={isSaving || !hasConfigRole} value={draft.name} onChange={(event) => updateDraft("name", event.target.value)} />
                              </label>
                              <label className="form-field-wide">
                                Description
                                <textarea disabled={isSaving || !hasConfigRole} value={draft.description} onChange={(event) => updateDraft("description", event.target.value)} />
                              </label>
                              <label>
                                Source Type
                                <select disabled={isSaving || !hasConfigRole} value={draft.sourceType} onChange={(event) => updateDraft("sourceType", event.target.value as SourceType)}>
                                  {sourceTypes.map((value) => <option key={value} value={value}>{value}</option>)}
                                </select>
                              </label>
                              <label>
                                Source Name
                                <input disabled={isSaving || !hasConfigRole} value={draft.sourceName} onChange={(event) => updateDraft("sourceName", event.target.value)} />
                              </label>
                              <label>
                                Workspace Name
                                <input disabled={isSaving || !hasConfigRole} value={draft.workspaceName || ""} onChange={(event) => updateDraft("workspaceName", event.target.value || undefined)} />
                              </label>
                              <label>
                                Pipeline Name
                                <input disabled={isSaving || !hasConfigRole} value={draft.pipelineName || ""} onChange={(event) => updateDraft("pipelineName", event.target.value || undefined)} />
                              </label>
                              <label>
                                Trigger Name
                                <input disabled={isSaving || !hasConfigRole} value={draft.triggerName || ""} onChange={(event) => updateDraft("triggerName", event.target.value || undefined)} />
                              </label>
                              <label>
                                Expected Time
                                <input disabled={isSaving || !hasConfigRole} value={draft.expectedStartTime} onChange={(event) => updateDraft("expectedStartTime", event.target.value)} />
                              </label>
                              <label>
                                Cron Expression
                                <input disabled={isSaving || !hasConfigRole} value={draft.cronExpression} onChange={(event) => updateDraft("cronExpression", event.target.value)} />
                              </label>
                              <label>
                                Grace Minutes
                                <input disabled={isSaving || !hasConfigRole} type="number" value={draft.graceMinutes} onChange={(event) => updateDraft("graceMinutes", Number(event.target.value))} />
                              </label>
                              <label>
                                Expected Duration Minutes
                                <input disabled={isSaving || !hasConfigRole} type="number" value={draft.expectedDurationMinutes} onChange={(event) => updateDraft("expectedDurationMinutes", Number(event.target.value))} />
                              </label>
                              <label>
                                Severity
                                <select disabled={isSaving || !hasConfigRole} value={draft.severity} onChange={(event) => updateDraft("severity", event.target.value as Severity)}>
                                  {severityOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                                </select>
                              </label>
                              <label>
                                Status
                                <select disabled={isSaving || !hasConfigRole} value={draft.status} onChange={(event) => updateDraft("status", event.target.value as IntegrationStatus)}>
                                  {statusOptions.map((value) => (
                                    <option key={value} value={value}>{value}</option>
                                  ))}
                                </select>
                              </label>
                              <label>
                                Owner
                                <input value={DEFAULT_JOB_OWNER} readOnly />
                              </label>
                              <label className="form-field-wide">
                                Raw Payload Path
                                <input disabled={isSaving || !hasConfigRole} value={draft.rawPayloadPath || ""} onChange={(event) => updateDraft("rawPayloadPath", event.target.value || undefined)} />
                              </label>
                              <button className="toggle-button" disabled={isSaving || !hasConfigRole} type="button" onClick={() => updateDraft("isActive", !draft.isActive)}>
                                {draft.isActive ? <ToggleRight size={18} aria-hidden="true" /> : <ToggleLeft size={18} aria-hidden="true" />}
                                {draft.isActive ? "Active" : "Inactive"}
                              </button>
                              <button className="primary-button" disabled={isSaving || !hasConfigRole} type="button" onClick={handleSave}>
                                <Save size={16} aria-hidden="true" />
                                {isSaving ? "Saving..." : "Save Config"}
                              </button>
                              <div className="job-action-card form-field-wide">
                                <div>
                                  <span>Operator action</span>
                                  <strong>Start a new Container Apps job execution for this job.</strong>
                                </div>
                                <button
                                  className="primary-button"
                                  disabled={!canRerunSelectedJob || isSaving || isRerunning}
                                  onClick={handleRerun}
                                  type="button"
                                >
                                  <Play size={15} aria-hidden="true" />
                                  {isRerunning ? "Requesting..." : "Rerun Job"}
                                </button>
                                <small>{rerunMessage || (canRerunSelectedJob ? "Ready to request a rerun." : rerunUnavailableReason)}</small>
                              </div>
                            </div>
                          </section>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </section>
  );
}
